<template>
    <modal size="lg" :id="modalId" title="Damage Notice" @shown="onModalShown" @hidden="onModalHidden">
        <busy-modal :show="busy" />
        <div class="damage-notice">
            <p class="damage-notice__description">
                {{ description || 'No description' }}
            </p>
            <div v-if="generatedPhotos.length > 0" class="damage-notice__gallery-wrapper">
                <photoswipe
                    ref="photoswipe"
                    class="damage-notice__gallery-wrapper__gallery"
                    gallery-id="damage-notice-gallery"
                    :data-source="photoSwipeDataSource"
                    v-slot="{ open }"
                >
                    <div class="damage-notice__gallery-wrapper__gallery__item">
                        <div class="row damage-notice__gallery-wrapper__gallery__item__top-info">
                            <div class="col-6 damage-notice__gallery-wrapper__gallery__item__top-info__name">
                                {{ getPhotoNameDisplay(selectedPhoto.photo_name) }}
                            </div>
                            <div class="col-6 damage-notice__gallery-wrapper__gallery__item__top-info__created">
                                {{ selectedPhoto.created_time }} {{ selectedPhoto.created_date }}
                            </div>
                        </div>
                        <div class="damage-notice__gallery-wrapper__gallery__item__photo-wrapper">
                            <a
                                :data-id="selectedPhoto.id"
                                :key="selectedPhoto.id"
                                :href="selectedPhoto.photo"
                                :data-pswp-width="selectedPhoto.photo_width"
                                :data-pswp-height="selectedPhoto.photo_height"
                                @click.prevent="open(selectedPhoto.index)"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <img
                                    :src="selectedPhoto.photo"
                                    :alt="selectedPhoto.caption"
                                    onerror="this.src='/static/images/image-placeholder.png'"
                                />
                            </a>
                        </div>
                        <div class="damage-notice__gallery-wrapper__gallery__item__bottom-info pb-3">
                            {{ selectedPhoto.caption || `Damage Photo ${selectedPhoto.index + 1}` }}
                        </div>
                    </div>
                </photoswipe>
                <div class="damage-notice__gallery-wrapper__slider pt-3" :style="sliderCssProps">
                    <div class="damage-notice__gallery-wrapper__slider__prev mr-3">
                        <b-button
                            variant="primary"
                            size="md"
                            :disabled="!canPrevPhoto"
                            @click="prevPhoto"
                        >
                            <font-awesome-icon :icon="['fas', 'chevron-left']" size="lg"></font-awesome-icon>
                        </b-button>
                    </div>
                    <transition-group
                        name="thumbnails"
                        tag="div"
                        class="row damage-notice__gallery-wrapper__slider__thumbnails"
                    >
                        <div v-for="photo in visiblePhotos" :key="photo.id" class="col-6 col-lg-3 thumbnail-wrapper">
                            <div
                                class="damage-notice__gallery-wrapper__slider__thumbnails__thumbnail"
                                :class="{ active: photo.selected }"
                                @click="selectPhoto(photo.id)"
                            >
                                <img
                                    :src="photo.thumbnail"
                                    :alt="photo.caption"
                                    onerror="this.src='/static/images/image-placeholder.png'"
                                />
                            </div>
                        </div>
                    </transition-group>
                    <div class="damage-notice__gallery-wrapper__slider__next ml-3">
                        <b-button
                            variant="primary"
                            size="md"
                            :disabled="!canNextPhoto"
                            @click="nextPhoto"
                        >
                            <font-awesome-icon :icon="['fas', 'chevron-right']" size="lg"></font-awesome-icon>
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <template #modal-footer="{ ok }">
            <b-button
                @click="$emit('show-work-order')"
                title="Show Work Order"
                size="md"
                variant="primary"
                v-if="showWorkOrderButton"
            >
                <font-awesome-icon :icon="['fas', 'file']" size="lg"></font-awesome-icon>
            </b-button>
            <b-button
                @click="$emit('create-work-order')"
                title="Create Work Order"
                size="md"
                variant="primary"
                v-if="createWorkOrderButton"
            >
                <font-awesome-icon :icon="['fas', 'file-circle-plus']" size="lg"></font-awesome-icon>
            </b-button>
            <b-button @click="$emit('print')" title="Print" size="md" variant="primary" v-if="printDamageNoticeButton">
                <font-awesome-icon :icon="['fas', 'print']" size="lg"></font-awesome-icon>
            </b-button>
            <b-button size="md" variant="primary" @click="ok()"> OK </b-button>
        </template>
    </modal>
</template>

<script>
import BusyModal from '../generic/busy-modal/busy-modal.vue';
import debounce from 'lodash/debounce';
import Modal from '../generic/modal/modal.vue';
import Photoswipe from '../generic/photoswipe/photoswipe.vue';

export default {
    name: 'DamageNoticeModal',
    components: {
        BusyModal,
        Modal,
        Photoswipe,
    },
    props: {
        busy: {
            type: Boolean,
            default: false,
        },
        createWorkOrderButton: {
            type: Boolean,
            default: false,
        },
        showWorkOrderButton: {
            type: Boolean,
            default: false,
        },
        description: String,
        modalId: String,
        photos: Array,
        printDamageNoticeButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            generatedPhotos: [],
            currentIndex: 0,
            imagesPerPage: 4,
            sliderLastAction: 'next',
        };
    },
    computed: {
        canNextPhoto() {
            return (
                this.currentIndex !== this.generatedPhotos.length - this.imagesPerPage &&
                this.generatedPhotos.length > this.imagesPerPage
            );
        },
        canPrevPhoto() {
            return this.currentIndex !== 0 && this.generatedPhotos.length > this.imagesPerPage;
        },
        photoSwipeDataSource() {
            return this.generatedPhotos.map((photo, index) => ({
                id: photo.id,
                src: photo.photo,
                alt: photo.caption || `Damage Photo ${index + 1}`,
                width: photo.photo_width,
                height: photo.photo_height,
            }));
        },
        selectedPhoto() {
            return this.generatedPhotos.find((photo) => photo.selected);
        },
        visiblePhotos() {
            return this.generatedPhotos.slice(this.currentIndex, this.currentIndex + this.imagesPerPage);
        },
        sliderCssProps() {
            return {
                '--thumbnails-enter': this.sliderLastAction == 'next' ? '100%' : '-100%',
                '--thumbnails-leave-to': this.sliderLastAction == 'next' ? '-100%' : '100%',
                '--thumbnails-leave-active': this.sliderLastAction == 'next' ? 'absolute' : 'static',
            };
        },
    },
    methods: {
        nextPhoto() {
            if (this.generatedPhotos.length - this.imagesPerPage > this.currentIndex) {
                this.sliderLastAction = 'next';
                this.currentIndex += 1;
            }
        },
        prevPhoto() {
            if (this.currentIndex != 0) {
                this.sliderLastAction = 'prev';
                this.currentIndex -= 1;
            }
        },
        selectPhoto(id) {
            const selectedPhoto = this.generatedPhotos.find((photo) => photo.selected);
            const newlySelectedPhoto = this.generatedPhotos.find((photo) => photo.id === id);
            selectedPhoto.selected = false;
            newlySelectedPhoto.selected = true;
        },
        getPhotoNameDisplay(name) {
            const extension = name.split('.').pop();
            if (name.length > 10) {
                return `${name.slice(0, 10)}...${extension}`;
            }
            return name;
        },
        handleResize() {
            const lg = 992;
            if (window.innerWidth < lg) {
                this.imagesPerPage = 2;
            } else {
                this.imagesPerPage = 4;
            }
            if (this.generatedPhotos.length - this.currentIndex < this.imagesPerPage) {
                this.currentIndex = Math.max(0, this.generatedPhotos.length - this.imagesPerPage);
            }
        },
        onModalShown() {
            this.generatedPhotos = this.photos.map((photo, index) => {
                return {
                    ...photo,
                    index: index,
                    selected: index === 0 ? true : false,
                };
            });
            this.handleResize();
            this.handleResize = debounce(this.handleResize, 100);
            window.addEventListener('resize', this.handleResize);
        },
        onModalHidden() {
            this.generatedPhotos = [];
            window.removeEventListener('resize', this.handleResize);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.damage-notice {
    &__description {
        color: #3d3d3d;
        font-size: 16px;
        line-height: 19.2px;
        padding-bottom: 1.5rem;
        margin: 0;
    }
    &__gallery-wrapper {
        padding: 1.5rem 0 1rem;
        &__gallery {
            &__item {
                &__top-info {
                    margin-bottom: 0.5rem;
                    align-items: center;
                    color: #717171;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 16.8px;
                    &__name {
                        word-break: break-all;
                    }
                    &__created {
                        text-align: right;
                    }
                }
                &__photo-wrapper {
                    margin-top: 4px;
                    height: 435px;
                    border-radius: 4px;
                    overflow: hidden;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }
                &__bottom-info {
                    margin-top: 0.5rem;
                    color: #3d3d3d;
                    font-size: 16px;
                    line-height: 19.2px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                }
            }
        }
        &__slider {
            height: 110px;
            display: flex;
            &__prev,
            &__next {
                display: flex;
                justify-content: center;
                align-items: center;
            }
            &__thumbnails {
                width: 100%;
                position: relative;
                flex-wrap: nowrap !important;
                overflow: hidden;
                &__thumbnail {
                    height: 100%;
                    padding: 3px;
                    cursor: pointer;
                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 6px;
                        transition: 0.1s;
                        outline: 0 solid #0d47a1;
                    }
                    &.active img {
                        outline-width: 3px;
                    }
                }
               
            }
        }
    }
    .thumbnails-move {
        transition: transform 0.3s;
    }
    .thumbnail-wrapper {
        transition: all 0.3s;
    }
    .thumbnails-enter {
        transform: translateX(var(--thumbnails-enter));
    }
    .thumbnails-leave-to {
        transform: translateX(var(--thumbnails-leave-to));
    }
    .thumbnails-leave-active {
        position: var(--thumbnails-leave-active);
        width: 100%;
        height: 100%;
    }
}

@include media-breakpoint-down(lg) {
    .damage-notice {
        &__gallery-wrapper {
            &__gallery {
                &__item {
                    &__photo-wrapper {
                        height: 263px;
                    }
                }
            }
        }
    }
}
</style>
